import { Component, EventEmitter, Input, OnChanges, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { environment } from 'src/environments/environment';
import { UserProfileService } from '../../service/user-profile.service';
import { UserProfile } from '../../interface/user-profile';

@Component({
  selector: 'app-profile-header',
  templateUrl: './profile-header.component.html',
  styleUrls: ['./profile-header.component.css'],
})
export class ProfileHeaderComponent implements OnInit, OnChanges {
  @Input() user: UserProfile | null = null;
  @Input() mode: 'view' | 'edit' = 'view';
  @Output() profileUpdated = new EventEmitter<UserProfile>();

  isEditModalOpen = false;
  profileForm!: FormGroup;
  previewImage: string | null = null;
  newImageFile: File | null = null;
  newCvFile: File | null = null;
  isSaving = false;
  saveError: string | null = null;

  constructor(
    private fb: FormBuilder,
    private userProfileService: UserProfileService,
  ) {}

  ngOnInit(): void {
    this.initForm();
  }

  ngOnChanges(): void {
    if (this.user && this.profileForm) {
      this.initForm();
    }
  }

  initForm(): void {
    this.profileForm = this.fb.group({
      name: [
        this.user?.name || '',
        [Validators.required, Validators.minLength(2)],
      ],
      email: [this.user?.email || '', [Validators.required, Validators.email]],
      phoneNumber: [this.user?.phoneNumber || '', Validators.required],
      specialization: [this.user?.specialization || '', Validators.required],
      city: [this.user?.city || ''],
      country: [this.user?.country || '', Validators.required],
      openToWork: [this.user?.openToWork ?? false],
      linkedInLink: [
        this.user?.linkedInLink || '',
        [Validators.pattern(/^https?:\/\/(www\.)?linkedin\.com\/.*$/i)],
      ],
    });
  }

  getUserImage(pictureUrl: string | null | undefined): string {
    if (!pictureUrl) {
      return this.generateAvatarWithLetter();
    }
    return environment.apiImagePath + pictureUrl;
  }

  generateAvatarWithLetter(): string {
    const name = this.user?.name || 'U';
    const firstLetter = name.charAt(0).toUpperCase();

    // Array of colors for consistency
    const colors = [
      '#f98650',
      '#d0385e',
      '#0000ff',
      '#15a72b',
      '#9b59b6',
      '#3498db',
      '#e74c3c',
      '#f39c12',
      '#1abc9c',
      '#34495e',
    ];

    // Generate consistent color based on name's first character
    const colorIndex = name.charCodeAt(0) % colors.length;
    const bgColor = colors[colorIndex];

    const svg = `
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 85 85">
        <rect width="85" height="85" fill="${bgColor}" rx="50%"/>
        <text x="48" y="56" font-size="32" font-weight="500" 
              font-family="Rubik, sans-serif" fill="#fff" 
              text-anchor="middle" dx="-0.2em">
          ${firstLetter}
        </text>
      </svg>
    `;

    return `data:image/svg+xml;base64,${btoa(svg)}`;
  }

  getCvUrl(cvUrl: string | null | undefined): string {
    if (!cvUrl) return '';
    return environment.apiImagePath + cvUrl;
  }

  downloadCv(): void {
    if (!this.user?.cvUrl) return;
    const url = this.getCvUrl(this.user.cvUrl);
    const link = document.createElement('a');
    link.href = url;
    link.target = '_blank';
    link.download = 'CV';
    link.click();
  }

  getUserLocation(
    city: string | null | undefined,
    country: string | null | undefined,
  ): string {
    if (city && country) {
      return `${city}, ${country}`;
    } else if (country) {
      return country;
    } else {
      return '';
    }
  }

  openEditModal(): void {
    this.isEditModalOpen = true;
  }

  closeEditModal(): void {
    this.resetModal();
    this.isEditModalOpen = false;
  }

  onImageSelect(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];

    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        alert('Image size must be less than 5MB');
        return;
      }
      if (!file.type.startsWith('image/')) {
        alert('Please select a valid image file');
        return;
      }

      this.newImageFile = file;

      const reader = new FileReader();
      reader.onload = (e) => {
        this.previewImage = e.target?.result as string;
      };
      reader.readAsDataURL(file);
    }
  }

  onCvSelect(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];

    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        alert('File size must be less than 5MB');
        return;
      }

      const allowedTypes = [
        'application/pdf',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      ];
      if (!allowedTypes.includes(file.type)) {
        alert('Please select a valid PDF or Word document');
        return;
      }

      this.newCvFile = file;
    }
  }

  removeCv(): void {
    this.newCvFile = null;
  }

  saveProfile(): void {
    // Validate form before sending
    if (this.profileForm.invalid) {
      this.profileForm.markAllAsTouched();
      return;
    }

    this.isSaving = true;
    this.saveError = null;
    const formData = new FormData();

    const profileData = this.profileForm.value;
    formData.append('name', profileData.name || '');
    formData.append('email', profileData.email || '');
    formData.append('phoneNumber', profileData.phoneNumber || '');
    formData.append('specialization', profileData.specialization || '');
    formData.append('city', profileData.city || '');
    formData.append('country', profileData.country || '');
    formData.append('openToWork', String(profileData.openToWork ?? false));
    formData.append('linkedInLink', profileData.linkedInLink || '');

    if (this.newImageFile) {
      formData.append('picture', this.newImageFile);
    }
    if (this.newCvFile) {
      formData.append('cv', this.newCvFile);
    }

    this.userProfileService.updateUserProfile(formData).subscribe({
      next: (response) => {
        this.isSaving = false;
        this.user = response;
        // Notify parent so skills, contact, completion all refresh
        this.profileUpdated.emit(response);
        this.closeEditModal();
      },
      error: (error) => {
        this.isSaving = false;
        this.saveError = 'Failed to update profile. Please try again.';
        console.error('Error updating profile:', error);
      },
    });
  }

  resetModal(): void {
    this.previewImage = null;
    this.newImageFile = null;
    this.newCvFile = null;
    this.initForm();
  }
}
